import { useState } from 'react';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: 'easeOut' },
  },
};

const inputStyle = {
  width: '100%',
  backgroundColor: '#0D1220',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '8px',
  padding: '12px 16px',
  color: '#FFFFFF',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.2s',
};

const labelStyle = {
  display: 'block',
  color: '#D1D5DB',
  fontSize: '13px',
  fontWeight: '500',
  marginBottom: '6px',
  letterSpacing: '0.03em',
};

export default function Contact() {
  const [formData, setFormData] = useState({
    'full-name': '',
    phone: '',
    email: '',
    vehicle: '',
    rego: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const encode = (data) =>
    Object.keys(data)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(false);

    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'contact-form', ...formData }),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#0A0F1C' }}>
      {/* Hero banner image */}
      <section className="relative flex items-center justify-center" style={{ minHeight: '320px', paddingTop: '64px' }}>
        <img
          src="/exterior.jpg"
          alt="Auto Doctor workshop exterior"
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: 'cover', objectPosition: 'center' }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.40)' }} />
        <motion.div
          className="relative z-10 text-center px-4"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
          >
            Get in Touch or Book a Service
          </h1>
          <p className="text-gray-200 text-lg mt-4 max-w-xl mx-auto">
            Fill in the form below and we will get back to you promptly. Or call us on 04-293 5987.
          </p>
        </motion.div>
      </section>

      {/* Main content grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* Form */}
            <motion.div
              className="w-full lg:w-3/5"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div
                className="rounded-2xl p-8 sm:p-10"
                style={{ backgroundColor: '#0D1220', border: '1px solid rgba(204,0,0,0.2)' }}
              >
                {submitted ? (
                  <div className="text-center py-12">
                    <div
                      className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                      style={{ backgroundColor: 'rgba(204,0,0,0.15)' }}
                    >
                      <svg className="w-8 h-8" style={{ color: '#CC0000' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h2
                      className="text-3xl font-bold text-white mb-3"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      Message Sent
                    </h2>
                    <p className="text-gray-400 text-base">
                      Thanks for getting in touch. We will be in contact with you shortly.
                    </p>
                  </div>
                ) : (
                  <form
                    name="contact-form"
                    method="POST"
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    onSubmit={handleSubmit}
                  >
                    {/* Netlify hidden fields */}
                    <input type="hidden" name="form-name" value="contact-form" />
                    <div style={{ display: 'none' }}>
                      <label>Do not fill this out: <input name="bot-field" /></label>
                    </div>

                    <h2
                      className="text-2xl font-bold text-white mb-8"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      Your Details
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                      <div>
                        <label htmlFor="full-name" style={labelStyle}>Full Name *</label>
                        <input
                          id="full-name"
                          type="text"
                          name="full-name"
                          required
                          placeholder="Your full name"
                          value={formData['full-name']}
                          onChange={handleChange}
                          style={inputStyle}
                          onFocus={(e) => { e.target.style.borderColor = '#CC0000'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" style={labelStyle}>Phone Number *</label>
                        <input
                          id="phone"
                          type="tel"
                          name="phone"
                          required
                          placeholder="Your phone number"
                          value={formData.phone}
                          onChange={handleChange}
                          style={inputStyle}
                          onFocus={(e) => { e.target.style.borderColor = '#CC0000'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                        />
                      </div>
                    </div>

                    <div className="mb-5">
                      <label htmlFor="email" style={labelStyle}>Email Address *</label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        required
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        style={inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = '#CC0000'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      />
                    </div>

                    <div className="mb-5">
                      <label htmlFor="vehicle" style={labelStyle}>Vehicle Make and Model *</label>
                      <input
                        id="vehicle"
                        type="text"
                        name="vehicle"
                        required
                        placeholder="e.g. Toyota Corolla 2019"
                        value={formData.vehicle}
                        onChange={handleChange}
                        style={inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = '#CC0000'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      />
                    </div>

                    <div className="mb-5">
                      <label htmlFor="rego" style={labelStyle}>Vehicle Rego *</label>
                      <input
                        id="rego"
                        type="text"
                        name="rego"
                        required
                        placeholder="e.g. ABC123"
                        value={formData.rego}
                        onChange={handleChange}
                        style={inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = '#CC0000'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      />
                    </div>

                    <div className="mb-5">
                      <label htmlFor="service" style={labelStyle}>Service Required *</label>
                      <select
                        id="service"
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleChange}
                        style={{ ...inputStyle, cursor: 'pointer' }}
                        onFocus={(e) => { e.target.style.borderColor = '#CC0000'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      >
                        <option value="" disabled>Select a service</option>
                        <option value="Mechanical Repairs">Mechanical Repairs</option>
                        <option value="Warrant of Fitness">Warrant of Fitness</option>
                        <option value="Vehicle Servicing">Vehicle Servicing</option>
                        <option value="Diagnostics">Diagnostics</option>
                        <option value="Tyres">Tyres</option>
                        <option value="Wheel Alignment">Wheel Alignment</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="mb-7">
                      <label htmlFor="message" style={labelStyle}>Message</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        placeholder="Tell us more about what your vehicle needs..."
                        value={formData.message}
                        onChange={handleChange}
                        style={{ ...inputStyle, resize: 'vertical' }}
                        onFocus={(e) => { e.target.style.borderColor = '#CC0000'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      />
                    </div>

                    {error && (
                      <p className="text-sm mb-4" style={{ color: '#CC0000' }}>
                        Something went wrong. Please try again or call us on 04-293 5987.
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 font-bold text-white rounded text-base transition-all duration-200"
                      style={{
                        backgroundColor: submitting ? '#AA0000' : '#CC0000',
                        fontFamily: "'Barlow Condensed', sans-serif",
                        letterSpacing: '0.05em',
                        fontSize: '16px',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                      }}
                      onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = '#AA0000'; }}
                      onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = '#CC0000'; }}
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Sidebar: contact details, hours, map */}
            <motion.div
              className="w-full lg:w-2/5 flex flex-col gap-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              {/* Contact details */}
              <div
                className="rounded-2xl p-7"
                style={{ backgroundColor: '#0D1220', border: '1px solid rgba(204,0,0,0.2)' }}
              >
                <h3
                  className="text-xl font-bold text-white mb-5"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Contact Details
                </h3>
                <ul className="space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex-shrink-0" style={{ color: '#CC0000' }}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </span>
                    <span className="text-gray-300">22 Parata Street, Waikanae, Kapiti Coast</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span style={{ color: '#CC0000' }}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </span>
                    <a href="tel:042935987" className="text-gray-300 hover:text-white transition-colors duration-200">04-293 5987</a>
                  </li>
                  <li className="flex items-center gap-3">
                    <span style={{ color: '#CC0000' }}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </span>
                    <a href="mailto:admin@autodoctor.co.nz" className="text-gray-300 hover:text-white transition-colors duration-200">admin@autodoctor.co.nz</a>
                  </li>
                </ul>
              </div>

              {/* Business hours */}
              <div
                className="rounded-2xl p-7"
                style={{ backgroundColor: '#0D1220', border: '1px solid rgba(204,0,0,0.2)' }}
              >
                <h3
                  className="text-xl font-bold text-white mb-5"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Business Hours
                </h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex justify-between">
                    <span className="text-gray-400">Monday to Friday</span>
                    <span className="text-white font-medium">8:00am to 5:00pm</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-400">Saturday</span>
                    <span className="text-gray-500">Closed</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-400">Sunday</span>
                    <span className="text-gray-500">Closed</span>
                  </li>
                </ul>
              </div>

              {/* Google Maps embed */}
              <div
                className="rounded-2xl overflow-hidden"
                style={{ border: '1px solid rgba(204,0,0,0.2)' }}
              >
                <iframe
                  title="Auto Doctor Waikanae Location"
                  src="https://maps.google.com/maps?q=Auto+Doctor+Waikanae&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="260"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
